import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { CaretRight, CaretLeft } from "@phosphor-icons/react";
import { VideoCard } from "@/components/VideoCard";
import { fetchVideos } from "@/lib/api";

export const GenreRow = ({ title, tag, fetchArgs, onViewAll, queryFn, anchorId, override, sortLabel, items: preset }) => {
  const args = { ...(fetchArgs || { page: 1, limit: 12, search: tag, mix: true }), ...override };
  const scroller = useRef(null);
  const { data, isLoading: loading } = useQuery({
    queryKey: ["genre", tag || title, JSON.stringify(args)],
    queryFn: queryFn || (() => fetchVideos(args)),
    enabled: !preset,
  });

  const items = preset || data?.items || [];
  const isLoading = !preset && loading;
  if (!isLoading && items.length === 0) return null;

  const slug = tag || title.toLowerCase().replace(/\s+/g, "-");
  const nudge = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  const Arrow = ({ dir, side, Icon }) => (
    <button
      type="button"
      tabIndex={-1}
      aria-hidden="true"
      data-testid={`row-scroll-${side}-${slug}`}
      onClick={() => nudge(dir)}
      className={`absolute top-0 bottom-4 z-30 hidden w-12 items-center justify-center text-white/80 opacity-0 transition-opacity duration-300 hover:text-amber md:flex group-hover/row:opacity-100 ${
        side === "left"
          ? "left-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent"
          : "right-0 bg-gradient-to-l from-[#050505] via-[#050505]/80 to-transparent"
      }`}
    >
      <Icon size={30} weight="bold" />
    </button>
  );

  return (
    <section id={anchorId} className="group/row mb-14 scroll-mt-36" data-testid={`genre-row-${slug}`}>
      <div className="mb-4 flex items-baseline justify-between">
        <div className="flex min-w-0 items-baseline gap-3">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-[#EDEDED]">{title}</h2>
          {sortLabel && (
            <span
              data-testid={`sort-badge-${slug}`}
              className="shrink-0 rounded-full border border-amber/30 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-amber/70"
            >
              Sorted by {sortLabel}
            </span>
          )}
        </div>
        {onViewAll && (
          <button
            data-testid={`view-all-${slug}`}
            onClick={onViewAll}
            className="flex shrink-0 items-center gap-1 font-mono text-[11px] uppercase tracking-[0.15em] text-white/40 transition-colors duration-300 hover:text-amber focus:outline-none focus:ring-2 focus:ring-amber"
          >
            View All <CaretRight size={14} />
          </button>
        )}
      </div>

      <div className="relative">
        <Arrow dir={-1} side="left" Icon={CaretLeft} />
        <div
          ref={scroller}
          className="flex gap-4 rv-snap-row overflow-x-auto scroll-smooth hide-scrollbar py-4 -mx-4 px-4"
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rv-row-card w-[240px] sm:w-[300px] shrink-0">
                  <div className="aspect-video bg-ink-surface animate-pulse border border-white/5" />
                </div>
              ))
            : items.map((v, i) => (
                <div key={v.video_id} className="rv-row-card w-[240px] sm:w-[300px] shrink-0">
                  <VideoCard video={v} index={i} />
                </div>
              ))}
        </div>
        <Arrow dir={1} side="right" Icon={CaretRight} />
      </div>
    </section>
  );
};
